import {
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import type { UUID } from "../../../utils/uuid";
import type { EmployeeDataFilter, EmployeeDataPageCursor } from "../server/supabase-person-service";

type EmployeeDataGridQuery = {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  selectedAudience: UUID | null;
  cursor: EmployeeDataPageCursor | null;
  sort: { field: string; direction: "asc" | "desc" };
  filter: EmployeeDataFilter;
  onPaginationModelChange: (
    model: GridPaginationModel,
    nextCursor: EmployeeDataPageCursor | null,
  ) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onFilterModelChange: (model: GridFilterModel) => void;
  onAudienceChange: (audience: UUID | null) => void;
};

export function useEmployeeDataGridQuery(defaultAudience: UUID | null): EmployeeDataGridQuery {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [selectedAudience, setSelectedAudience] = useState<UUID | null>(defaultAudience);
  const [pageStarts, setPageStarts] = useState<(EmployeeDataPageCursor | null)[]>([null]);

  const sort = useMemo(
    () =>
      sortModel[0]?.sort
        ? { field: sortModel[0].field, direction: sortModel[0].sort }
        : ({ field: "id", direction: "asc" } as const),
    [sortModel],
  );
  const filter = useMemo(() => toEmployeeDataFilter(filterModel), [filterModel]);

  const resetPage = (): void => {
    setPageStarts([null]);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  };

  const onPaginationModelChange = (
    model: GridPaginationModel,
    nextCursor: EmployeeDataPageCursor | null,
  ): void => {
    if (model.pageSize !== paginationModel.pageSize) {
      setPageStarts([null]);
      setPaginationModel({ page: 0, pageSize: model.pageSize });
      return;
    }
    if (model.page > paginationModel.page) {
      if (!nextCursor) return;
      setPageStarts((starts) => {
        const next = starts.slice(0, model.page);
        next[model.page] = nextCursor;
        return next;
      });
    }
    setPaginationModel(model);
  };

  const onSortModelChange = (model: GridSortModel): void => {
    resetPage();
    setSortModel(model);
  };

  const onFilterModelChange = (model: GridFilterModel): void => {
    resetPage();
    setFilterModel(model);
  };

  const onAudienceChange = (audience: UUID | null): void => {
    resetPage();
    setSelectedAudience(audience);
  };

  return {
    paginationModel,
    sortModel,
    filterModel,
    selectedAudience,
    cursor: pageStarts[paginationModel.page] ?? null,
    sort,
    filter,
    onPaginationModelChange,
    onSortModelChange,
    onFilterModelChange,
    onAudienceChange,
  };
}

export function toEmployeeDataFilter(model: GridFilterModel): EmployeeDataFilter {
  const items = model.items.flatMap(({ field, operator, value }) => {
    if (!field || !operator) return [];
    if (["isEmpty", "isNotEmpty"].includes(operator)) return [{ field, operator }];
    if (Array.isArray(value)) {
      const values = value
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim());
      return values.length === 0 ? [] : [{ field, operator, value: values }];
    }
    if (typeof value !== "string" || !value.trim()) return [];
    return [{ field, operator, value: value.trim() }];
  });
  return {
    items,
    ...(model.logicOperator ? { logicOperator: model.logicOperator } : {}),
  };
}
